import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtKES } from "@/lib/api";
import { FAQ_CATEGORIES } from "@/data/faq";
import {
    Command, CommandInput, CommandList, CommandItem,
    CommandGroup, CommandSeparator, CommandEmpty,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
    Users, MessagesSquare, TrendingUp, HelpCircle, Search,
} from "lucide-react";
import { signTone, toneClassName, signedPrefix } from "@/lib/numberColor";

/**
 * Global Cmd+K (Ctrl+K on Windows) search palette.
 *
 * Sources:
 *   - Backend /api/search (leaders, posts, trades — auth-scoped)
 *   - Static FAQ_CATEGORIES from /data/faq.js (client-side filter)
 *
 * Debounced 180ms, arrow-keyable via shadcn's Command primitive (cmdk).
 */
export default function CommandPalette({ open, onOpenChange }) {
    const nav = useNavigate();
    const [q, setQ] = useState("");
    const [results, setResults] = useState({ leaders: [], posts: [], trades: [] });
    const debounceRef = useRef();

    // Debounced backend fetch
    const fetchResults = useCallback(async (query) => {
        if (query.trim().length < 2) {
            setResults({ leaders: [], posts: [], trades: [] });
            return;
        }
        try {
            const { data } = await api.get(`/search?q=${encodeURIComponent(query)}&limit=5`);
            setResults(data);
        } catch { /* ignore — palette still shows FAQ */ }
    }, []);

    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchResults(q), 180);
        return () => clearTimeout(debounceRef.current);
    }, [q, fetchResults]);

    // Reset query when palette closes
    useEffect(() => { if (!open) setQ(""); }, [open]);

    // Client-side FAQ filter — flatten entries with their category label
    const faqMatches = useMemo(() => {
        if (q.trim().length < 2) return [];
        const needle = q.trim().toLowerCase();
        const out = [];
        for (const cat of FAQ_CATEGORIES) {
            for (const e of cat.entries) {
                if (
                    e.q.toLowerCase().includes(needle) ||
                    e.a.toLowerCase().includes(needle)
                ) {
                    out.push({ q: e.q, cat: cat.title, catKey: cat.key });
                    if (out.length >= 5) return out;
                }
            }
        }
        return out;
    }, [q]);

    function pick(url) {
        onOpenChange(false);
        nav(url);
    }

    const hasResults =
        results.leaders.length +
        results.posts.length + results.trades.length + faqMatches.length > 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0" data-testid="cmd-palette">
                <DialogTitle className="sr-only">Search Melian Dialogue</DialogTitle>
                <DialogDescription className="sr-only">
                    Search across leaders, community posts, your trades, and FAQ.
                </DialogDescription>
                <Command shouldFilter={false}>
                    <CommandInput
                        value={q}
                        onValueChange={setQ}
                        placeholder="Search leaders, posts, your trades, FAQ…"
                        data-testid="cmd-input"
                        autoFocus
                    />
                    <CommandList className="max-h-[60vh]">
                        {!q && (
                            <div className="px-4 py-6 text-xs text-muted-foreground">
                                <div className="mb-2 uppercase tracking-wider">Try searching for</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {["stop loss", "copy trading", "payouts", "SCOM", "leaderboard"].map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => setQ(s)}
                                            className="px-2 py-0.5 border border-border rounded hover:border-primary hover:text-primary transition-colors"
                                            data-testid={`cmd-suggest-${s.replace(/\s+/g, "-")}`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                                <div className="mt-6 text-[10px] leading-relaxed">
                                    <kbd className="mono px-1 rounded border border-border">↑</kbd>
                                    <kbd className="mono px-1 rounded border border-border ml-0.5">↓</kbd> to navigate ·
                                    <kbd className="mono px-1 rounded border border-border ml-1">↵</kbd> to open ·
                                    <kbd className="mono px-1 rounded border border-border ml-1">Esc</kbd> to close
                                </div>
                            </div>
                        )}

                        {q && !hasResults && (
                            <CommandEmpty>No results for "{q}"</CommandEmpty>
                        )}

                        {results.leaders.length > 0 && (
                            <CommandGroup heading="Copy leaders">
                                    {results.leaders.map((l) => (
                                        <CommandItem
                                            key={l.id}
                                            onSelect={() => pick(l.url)}
                                            data-testid={`cmd-leader-${l.id}`}
                                            className="cursor-pointer"
                                        >
                                            <Users className="w-4 h-4 mr-2 shrink-0 text-primary" />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold truncate">{l.name}</div>
                                                <div className="text-xs text-muted-foreground uppercase">{l.tier}</div>
                                            </div>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                        )}

                        {results.posts.length > 0 && (
                            <>
                                <CommandSeparator />
                                <CommandGroup heading="Community">
                                    {results.posts.map((p) => (
                                        <CommandItem
                                            key={p.id}
                                            onSelect={() => pick(p.url)}
                                            data-testid={`cmd-post-${p.id}`}
                                            className="cursor-pointer"
                                        >
                                            <MessagesSquare className="w-4 h-4 mr-2 shrink-0 text-primary" />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold truncate">{p.author}</div>
                                                <div className="text-xs text-muted-foreground truncate">{p.snippet}</div>
                                            </div>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </>
                        )}

                        {results.trades.length > 0 && (
                            <>
                                <CommandSeparator />
                                <CommandGroup heading="Your trades">
                                    {results.trades.map((t) => (
                                        <CommandItem
                                            key={t.id}
                                            onSelect={() => pick(t.url)}
                                            data-testid={`cmd-trade-${t.id}`}
                                            className="cursor-pointer"
                                        >
                                            <TrendingUp className={`w-4 h-4 mr-2 shrink-0 ${t.side === "buy" ? "text-success" : "text-danger"}`} />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold truncate">
                                                    {t.symbol} · <span className="uppercase">{t.side}</span>
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {t.status === "closed" ? (
                                                        <>Closed · PnL <span className={toneClassName(signTone(t.pnl, 0))}>{signedPrefix(t.pnl, 0)}{fmtKES(Math.abs(t.pnl))}</span></>
                                                    ) : "Open position"}
                                                </div>
                                            </div>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </>
                        )}

                        {faqMatches.length > 0 && (
                            <>
                                <CommandSeparator />
                                <CommandGroup heading="FAQ">
                                    {faqMatches.map((f, i) => (
                                        <CommandItem
                                            key={f.q || `faq-${i}`}
                                            onSelect={() => pick(`/faq?q=${encodeURIComponent(f.q)}`)}
                                            data-testid={`cmd-faq-${i}`}
                                            className="cursor-pointer"
                                        >
                                            <HelpCircle className="w-4 h-4 mr-2 shrink-0 text-primary" />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold truncate">{f.q}</div>
                                                <div className="text-xs text-muted-foreground truncate">{f.cat}</div>
                                            </div>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </DialogContent>
        </Dialog>
    );
}


/**
 * A small trigger button that opens the palette. Wire this into the topbar.
 * Also registers the global Cmd+K / Ctrl+K keyboard shortcut.
 */
export function CommandPaletteTrigger() {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        function onKey(e) {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setOpen((v) => !v);
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-2 rounded border border-border bg-secondary/40 hover:bg-secondary text-muted-foreground hover:text-foreground text-xs px-2.5 py-1.5 transition-colors"
                data-testid="cmd-trigger"
                aria-label="Open search"
            >
                <Search className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Search</span>
            </button>
            <CommandPalette open={open} onOpenChange={setOpen} />
        </>
    );
}
